import * as React from "react"
import { Check } from "lucide-react"

import { Badge } from "../../../internal/primitives/badge"
import { Button } from "../../../internal/primitives/button"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "../../../internal/primitives/command"
import { Popover, PopoverContent, PopoverTrigger } from "../../../internal/primitives/popover"
import { Separator } from "../../../internal/primitives/separator"
import { cn } from "../../../lib/utils"

export interface DataTableFacetedFilterProps {
  title?: string
  options: {
    label: string
    value: string
    icon?: React.ComponentType<{ className?: string }>
  }[]
  selectedValues: Set<string>
  onFilterChange: (values: Set<string>) => void
  /**
   * Fix #6: optional per-value counts shown alongside each option label.
   * Operators need to know whether filtering by "Inactive" returns 3 rows or 300
   * before committing to the filter. Pass a map of option value → row count.
   *
   * Example: { active: 124, inactive: 7, pending: 3 }
   */
  counts?: Record<string, number>
}

export function DataTableFacetedFilter({
  title,
  options,
  selectedValues,
  onFilterChange,
  counts,
}: DataTableFacetedFilterProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 border-dashed border-border/40 hover:border-border/60">
          <span className="text-muted-foreground/60 mr-1.5">{title}</span>
          {selectedValues?.size > 0 && (
            <>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <Badge variant="secondary" className="rounded-sm px-1 font-normal text-[10px] lg:hidden">
                {selectedValues.size}
              </Badge>
              <div className="hidden space-x-1 lg:flex">
                {selectedValues.size > 2 ? (
                  <Badge variant="secondary" className="rounded-sm px-1 font-normal text-[10px]">
                    {selectedValues.size} selected
                  </Badge>
                ) : (
                  options
                    .filter((option) => selectedValues.has(option.value))
                    .map((option) => (
                      <Badge variant="secondary" key={option.value} className="rounded-sm px-1 font-normal text-[10px]">
                        {option.label}
                      </Badge>
                    ))
                )}
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0 shadow-xl" align="start">
        <Command>
          {/* Fix #6: use a descriptive placeholder rather than repeating the title,
              which reads as "Filter Status" inside a "Status" popover. */}
          <CommandInput placeholder={title ? `Filter ${title.toLowerCase()}…` : "Search…"} className="text-xs" />
          <CommandList>
            <CommandEmpty className="py-4 text-center text-xs text-muted-foreground">No matches.</CommandEmpty>
            <CommandGroup className="p-1">
              {options.map((option) => {
                const isSelected = selectedValues.has(option.value)
                const count = counts?.[option.value]
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => {
                      const newSelectedValues = new Set(selectedValues)
                      if (isSelected) {
                        newSelectedValues.delete(option.value)
                      } else {
                        newSelectedValues.add(option.value)
                      }
                      onFilterChange(newSelectedValues)
                    }}
                    className="rounded-sm text-xs"
                  >
                    <div
                      className={cn(
                        "mr-2 flex h-3.5 w-3.5 items-center justify-center rounded-sm border border-primary/50",
                        isSelected
                          ? "bg-primary text-primary-foreground border-primary"
                          : "opacity-50 [&_svg]:invisible"
                      )}
                    >
                      <Check className="h-2.5 w-2.5" />
                    </div>
                    {option.icon && (
                      <option.icon className="mr-2 h-3.5 w-3.5 text-muted-foreground/60" />
                    )}
                    <span className="flex-1 truncate">{option.label}</span>
                    {/* Fix #6: render the count badge when counts are provided.
                        Users can immediately see which filter values are populated
                        before making a selection. */}
                    {count != null && (
                      <span className="ml-auto text-[10px] font-bold tabular-nums text-muted-foreground/50">
                        {count.toLocaleString()}
                      </span>
                    )}
                  </CommandItem>
                )
              })}
            </CommandGroup>
            {selectedValues.size > 0 && (
              <>
                <CommandSeparator className="m-0" />
                <CommandGroup className="p-1">
                  <CommandItem
                    onSelect={() => onFilterChange(new Set())}
                    className="justify-center text-center text-[11px] font-bold uppercase tracking-tight text-primary hover:bg-primary/5"
                  >
                    Clear filters
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
